import { useState } from "react";
import type { FormEvent } from "react";

import { useAuth } from "../context/auth";
import { useTheme } from "../context/theme";
import { useLocale } from "../context/locale";
import { LOCALE_LABELS, LOCALES } from "../i18n";
import { ApiError } from "../lib/api";
import { takeSignOutReason } from "../lib/session";
import { useToast } from "../context/toast";
import type { SignOutReason } from "../lib/session";
import type { TranslationKey } from "../i18n";
import { BrandLockup } from "../components/brand";
import { Button } from "../components/ui";
import { TextField } from "../components/ui/form";

/**
 * Which message explains each way a session can end.
 *
 * `manual` is absent on purpose: someone who signed out themselves does not
 * need to be told why they are looking at a sign-in screen.
 */
const SIGN_OUT_NOTICE: Partial<Record<SignOutReason, TranslationKey>> = {
  expired: "signIn.expired",
  rejected: "signIn.rejected",
  idle: "signIn.idle",
  // The one notice that is also a security warning — see the copy.
  superseded: "signIn.superseded",
};

export function SignIn() {
  const { signIn } = useAuth();
  const toast = useToast();
  const { theme, toggle } = useTheme();
  const { locale, setLocale, t } = useLocale();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read once on mount — the reason is consumed so a manual reload does not
  // keep re-announcing a timeout the operator has already seen.
  const [noticeKey] = useState(() => {
    const reason = takeSignOutReason();
    return reason ? (SIGN_OUT_NOTICE[reason] ?? null) : null;
  });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const displaced = await signIn(username.trim(), password);
      /*
       * Said out loud when this sign-in ended one somewhere else.
       *
       * The toast provider sits above the router, so it survives this screen
       * being replaced by the console a render later — which is the only way to
       * deliver a message that belongs to the moment of signing in.
       */
      if (displaced > 0) toast.push("info", t("signIn.displaced"), t("signIn.displacedDetail"));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : t("signIn.failed"),
      );
      setBusy(false);
    }
  }

  return (
    <div className="bg-app relative grid min-h-dvh place-items-center overflow-hidden p-5">
      {/* A single crimson bloom behind the card — the only decoration in the app. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60rem 40rem at 50% -10%, var(--color-crimson), transparent 70%)",
          opacity: 0.55,
        }}
        aria-hidden
      />

      <div
        className="absolute top-5 left-5 flex items-center overflow-hidden rounded-lg border"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        role="group"
        aria-label={t("app.language")}
      >
        {LOCALES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setLocale(code)}
            className="h-9 px-3 text-[12px] font-semibold transition-colors"
            style={
              locale === code
                ? {
                    background: "var(--color-crimson)",
                    color: "var(--color-gold-light)",
                  }
                : { color: "var(--text-muted)" }
            }
            aria-pressed={locale === code}
          >
            {LOCALE_LABELS[code]}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={toggle}
        className="text-muted absolute top-5 right-5 grid size-9 place-items-center rounded-lg border transition-colors hover:text-[var(--accent)]"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        aria-label={
          theme === "dark" ? t("app.theme.toLight") : t("app.theme.toDark")
        }
      >
        {theme === "dark" ? "☀" : "☾"}
      </button>

      <main className="panel relative w-full max-w-[24rem] p-7">
        <div className="mb-6">
          <BrandLockup
            subtitle={t("app.tagline")}
            size={34}
            wordmarkClass="text-[16px]"
          />
        </div>

        <h1 className="display text-[26px]" style={{ color: "var(--accent)" }}>
          {t("signIn.title")}
        </h1>
        <p className="text-muted mt-1 mb-6 text-[13px]">
          {t("signIn.subtitle")}
        </p>

        {noticeKey ? (
          <p
            className="mb-4 rounded-lg border px-3 py-2 text-[12px]"
            style={{
              background: "var(--warning-bg)",
              borderColor: "var(--warning)",
              color: "var(--warning)",
            }}
            role="status"
          >
            {t(noticeKey)}
          </p>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <TextField
            label={t("signIn.username")}
            value={username}
            onChange={setUsername}
            placeholder="admin"
            required
            autoFocus
            autoComplete="username"
          />

          <TextField
            label={t("signIn.password")}
            value={password}
            onChange={setPassword}
            type="password"
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />

          {error ? (
            <p
              className="rounded-lg border px-3 py-2 text-[12px]"
              style={{
                background: "var(--danger-bg)",
                borderColor: "var(--danger)",
                color: "var(--danger)",
              }}
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={busy}
            disabled={!username.trim() || !password}
          >
            {t("signIn.submit")}
          </Button>
        </form>

        <p
          className="text-dim mt-6 border-t pt-4 text-[11px]"
          style={{ borderColor: "var(--border)" }}
        >
          {t("signIn.seeded", {
            admin: "admin / admin12345",
            support: "support / support12345",
          })}
        </p>
      </main>
    </div>
  );
}
